'use client';

import { motion, useReducedMotion } from 'framer-motion';
import { useScrollReveal } from '@/app/hooks/use-scroll-reveal';
import { startTransition, useEffect, useRef, useState } from 'react';

const STATS = [
  { value: 26, suffix: '', label: 'Counties targeted' },
  { value: 1, suffix: '', label: 'Platform built' },
  { value: 100, suffix: '%', label: 'Irish-owned' },
  { value: 1, suffix: '', label: 'Mission' },
] as const;

const TAGS = ['All of Ireland', 'Founded 2024', 'Community-first', 'Schools \u00b7 Centres \u00b7 Councils'] as const;

function StatCell({ value, suffix, label, animate }: {
  value: number;
  suffix: string;
  label: string;
  animate: boolean;
}) {
  const prefersReducedMotion = useReducedMotion();
  const [displayValue, setDisplayValue] = useState(prefersReducedMotion ? value : 0);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!animate || hasAnimated.current || prefersReducedMotion) {
      startTransition(() => setDisplayValue(value));
      return;
    }
    hasAnimated.current = true;
    const duration = 1200;
    const start = performance.now();
    const step = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      startTransition(() => setDisplayValue(Math.round(eased * value)));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [animate, value, prefersReducedMotion]);

  return (
    <div className="flex flex-col" aria-label={`${value}${suffix} ${label}`}>
      <span className="font-display text-[clamp(36px,4vw,56px)] font-extrabold leading-none tracking-[-0.025em] text-sandron-ink-900">
        {displayValue}{suffix}
      </span>
      <span className="mt-2 text-[13px] font-medium uppercase tracking-[0.08em] text-sandron-ink-500">
        {label}
      </span>
    </div>
  );
}

export function AboutSnippet() {
  const { ref, isVisible } = useScrollReveal();
  const prefersReducedMotion = useReducedMotion();

  const fadeUp = (delay: number, y = 16) => ({
    initial: { opacity: 0, y: prefersReducedMotion ? 0 : y },
    animate: isVisible ? { opacity: 1, y: 0 } : { opacity: 0 },
    transition: {
      duration: prefersReducedMotion ? 0.15 : 0.6,
      delay: delay / 1000,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
    },
  });

  return (
    <section className="bg-sandron-ink-50 py-[clamp(80px,10vw,140px)]">
      <div
        ref={ref}
        className="mx-auto grid max-w-[1200px] items-start gap-12 px-5 md:px-10 lg:grid-cols-[1.1fr_1fr] lg:gap-20 lg:px-0"
      >
        {/* Text column */}
        <div>
          <motion.div {...fadeUp(0, 12)} className="flex items-center gap-3">
            <span className="h-px w-6 bg-sandron-ink-300" aria-hidden="true" />
            <span className="font-body text-xs font-semibold uppercase tracking-[0.12em] text-sandron-ink-500">
              About Sandron
            </span>
          </motion.div>

          <motion.h2
            {...fadeUp(90, 20)}
            className="mt-4 max-w-[540px] font-display text-[clamp(32px,4vw,56px)] font-bold leading-[1.1] tracking-[-0.02em] text-sandron-ink-900 [text-wrap:balance]"
          >
            Built in Dublin, for communities across Ireland.
          </motion.h2>

          <motion.div {...fadeUp(180, 16)} className="mt-5 max-w-[520px] space-y-4">
            <p className="text-[17px] leading-[1.65] text-sandron-ink-700">
              Sandron Infotech is a software studio that designs, builds, and
              operates digital products for community organisations. We&apos;re
              small by choice — it lets us move fast and stay close to the people
              who use what we make.
            </p>
            <p className="text-[17px] leading-[1.65] text-sandron-ink-700">
              Our flagship, SpaceNest, helps community centres manage bookings,
              memberships, and payments — all from one platform.
            </p>
          </motion.div>

          {/* Tag chips */}
          <motion.div {...fadeUp(360, 12)} className="mt-8 flex flex-wrap gap-2">
            {TAGS.map((tag) => (
              <span
                key={tag}
                className="rounded-full border border-sandron-ink-100 bg-white px-3.5 py-1.5 text-[13px] font-medium text-sandron-ink-700 transition-colors duration-200 hover:border-sandron-ink-300 hover:bg-sandron-ink-100"
              >
                {tag}
              </span>
            ))}
          </motion.div>
        </div>

        {/* Stats card */}
        <motion.div
          initial={{ opacity: 0, x: prefersReducedMotion ? 0 : 32 }}
          animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0 }}
          transition={{
            duration: prefersReducedMotion ? 0.15 : 0.7,
            delay: 0.2,
            ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
          }}
          className="rounded-3xl border border-sandron-ink-100 bg-white p-7 shadow-[0_4px_24px_-8px_rgba(11,18,32,0.06)] md:p-10"
        >
          <div className="grid grid-cols-2 gap-8">
            {STATS.map((stat, i) => (
              <div
                key={stat.label}
                className={`${i >= 2 ? 'border-t border-sandron-ink-100 pt-8' : ''}`}
              >
                <StatCell
                  value={stat.value}
                  suffix={stat.suffix}
                  label={stat.label}
                  animate={isVisible}
                />
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
